import React, { useState } from 'react';
import { PropertyFeature, Property } from '../../types/property';
import { formatCurrency, formatPercent, formatNumber } from '../../utils/formatting';
import { AnalysisAccordion } from '../Properties/AnalysisAccordion';

interface PropertyPopupProps {
  property: PropertyFeature;
  onClose: () => void;
}

// Mapbox GL serializes non-primitive GeoJSON properties to JSON strings — parse them back
function parseMapboxProp<T>(raw: any, fallback: T): T {
  if (raw === null || raw === undefined) return fallback;
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch { return fallback; }
  }
  return raw as T;
}

const PropertyPopup: React.FC<PropertyPopupProps> = ({ property, onClose }) => {
  const p = property.properties;
  const [photoIdx, setPhotoIdx] = useState(0);

  const photos: string[] = parseMapboxProp(p.photos, p.photo_url ? [p.photo_url] : []);
  const eb: any = parseMapboxProp(p.expense_breakdown, null);
  const brrrr: any = parseMapboxProp(p.brrrr, null);
  const appreciation: any = parseMapboxProp(p.appreciation, null);
  const estimates: any = parseMapboxProp(p.estimates, null);

  // Reconstruct a Property object so we can reuse AnalysisAccordion exactly as in the list view
  const propertyForAccordion: Property = {
    id: p.id,
    address: p.address,
    city: p.city,
    state: p.state,
    zip_code: p.zip_code,
    bedrooms: p.bedrooms,
    bathrooms: p.bathrooms,
    sqft: p.sqft,
    year_built: p.year_built,
    property_type: p.property_type,
    list_price: p.list_price || p.price,
    photo_url: p.photo_url,
    photos,
    arv: p.arv,
    estimated_rehab: p.estimated_rehab,
    market_rent: p.market_rent,
    section8_rent: p.section8_rent,
    property_tax_rate: p.property_tax_rate,
    estimates,
    total_project_cost: p.total_project_cost,
    monthly_cash_flow: p.monthly_cash_flow,
    monthly_mortgage: p.monthly_mortgage,
    expense_breakdown: eb,
    brrrr,
    appreciation,
    cap_rate: p.cap_rate,
    cash_on_cash_return: p.cash_on_cash_return,
    deal_spread: p.deal_spread,
    break_even_price: p.break_even_price,
    median_estimate: p.median_estimate,
    roi: p.roi,
    breakeven_years: p.breakeven_years,
    noi: p.noi,
  };

  const cf = p.monthly_cash_flow || 0;
  const annualCF = cf * 12;
  const rent = p.section8_rent || 0;
  const mortgage = p.monthly_mortgage || 0;
  const expTotal = eb?.total || 0;
  const displayPrice = p.list_price || p.price;
  const arv = p.median_estimate || p.arv;
  const price = displayPrice;
  const equityGap = arv && price ? arv - price : null;
  const equityPct = equityGap !== null && price ? (equityGap / price) * 100 : null;
  const annualNoi = p.noi || 0;
  const roiVal = p.roi || 0;

  const cfGradient =
    cf >= 500 ? 'bg-gradient-to-br from-green-500 to-emerald-600' :
    cf >= 300 ? 'bg-gradient-to-br from-green-400 to-emerald-500' :
    cf >= 0   ? 'bg-gradient-to-br from-orange-400 to-amber-500' :
                'bg-gradient-to-br from-red-500 to-rose-600';

  const card = (
    gradient: string,
    label: string,
    tooltip: React.ReactNode,
    value: React.ReactNode,
    badge?: React.ReactNode
  ) => (
    <div className={`rounded-xl p-2.5 shadow-sm text-white ${gradient}`}>
      <div className="flex items-start justify-between gap-1 mb-1">
        <span className="text-xs font-semibold opacity-80 uppercase tracking-wide leading-tight">{label}</span>
        <div className="relative group flex-shrink-0">
          <span className="text-white/50 cursor-help text-xs">i</span>
          <div className="pointer-events-none absolute bottom-full right-0 mb-2 hidden group-hover:block z-50 w-64 bg-gray-900 text-white text-xs rounded-lg p-2.5 shadow-xl">
            {tooltip}
            <div className="absolute top-full right-2 border-4 border-transparent border-t-gray-900" />
          </div>
        </div>
      </div>
      <div className="text-xl font-black leading-none">{value}</div>
      {badge && <div className="text-xs opacity-80 mt-1 font-medium">{badge}</div>}
    </div>
  );

  return (
    <div className="absolute inset-y-0 right-0 w-[30rem] bg-white shadow-2xl z-10 flex flex-col border-l border-gray-200">

      {/* Photo carousel */}
      <div className="relative h-56 bg-gray-100 flex-shrink-0 overflow-hidden">
        {photos.length > 0 ? (
          <>
            <img
              src={photos[photoIdx]}
              alt={p.address}
              className="w-full h-full object-contain bg-gray-100"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            {photos.length > 1 && (
              <>
                <button
                  onClick={() => setPhotoIdx(i => (i - 1 + photos.length) % photos.length)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => setPhotoIdx(i => (i + 1) % photos.length)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                  {photoIdx + 1} / {photos.length}
                </div>
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {photos.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setPhotoIdx(idx)}
                      className={`w-2 h-2 rounded-full transition-colors ${idx === photoIdx ? 'bg-white' : 'bg-white/50 hover:bg-white/75'}`}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">No photos</div>
        )}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white rounded-full w-8 h-8 flex items-center justify-center text-lg leading-none"
        >x</button>
      </div>

      <div className="overflow-y-auto flex-1 min-h-0 bg-white">
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1 min-w-0 pr-3">
              <h3 className="text-base font-bold text-gray-900 leading-tight">{p.address}</h3>
              <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
                <span>{p.city}{p.state ? `, ${p.state}` : ''} {p.zip_code}</span>
                {p.property_type && (
                  <span className="bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded capitalize">
                    {p.property_type.replace(/_/g, ' ')}
                  </span>
                )}
              </p>
            </div>
            <div className={`flex-shrink-0 px-2.5 py-1.5 rounded-full text-xs font-bold border flex items-center gap-1.5 ${
              cf >= 500 ? 'bg-green-100 text-green-800 border-green-300' :
              cf >= 300 ? 'bg-emerald-50 text-emerald-800 border-emerald-300' :
              cf >= 0   ? 'bg-orange-50 text-orange-700 border-orange-300' :
                          'bg-red-50 text-red-700 border-red-300'
            }`}>
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                cf >= 300 ? 'bg-green-500' : cf >= 0 ? 'bg-orange-500' : 'bg-red-500'
              }`}></span>
              {formatCurrency(cf)}/mo
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-xl font-black text-gray-900">{displayPrice ? formatCurrency(displayPrice) : 'Price N/A'}</span>
            {p.status && (
              <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded capitalize">{p.status.replace(/_/g, ' ')}</span>
            )}
            <span className="text-gray-300">·</span>
            <span className="text-sm text-gray-600">
              <strong className="text-gray-900">{p.bedrooms || '—'}</strong> bd /
              <strong className="text-gray-900"> {p.bathrooms || '—'}</strong> ba
            </span>
            {p.sqft && <><span className="text-gray-300">·</span><span className="text-sm text-gray-600"><strong className="text-gray-900">{formatNumber(p.sqft)}</strong> sqft</span></>}
            {p.year_built && <><span className="text-gray-300">·</span><span className="text-sm text-gray-600">Built <strong className="text-gray-900">{p.year_built}</strong></span></>}
          </div>
        </div>

        {/* Metrics */}
        <div className="p-4">
          <div className="text-xs text-gray-400 italic mb-2">* Assumes full occupancy using current rent assumptions. Adjust renovations and rent assumptions as needed.</div>
          <div className="grid grid-cols-2 gap-2">

            {card(
              rent >= 1500 ? 'bg-gradient-to-br from-green-600 to-emerald-700' :
              rent >= 1000 ? 'bg-gradient-to-br from-green-500 to-emerald-600' :
              rent > 0     ? 'bg-gradient-to-br from-green-400 to-emerald-500' :
                             'bg-gradient-to-br from-slate-400 to-slate-600',
              'Gross Rent/mo',
              <div><div className="font-bold">Estimated Rent</div><div className="opacity-75">Modeled rent for ZIP {p.zip_code} · {p.bedrooms ?? '?'}bd using HUD FMR benchmarks. Gross income before all expenses.</div></div>,
              formatCurrency(rent),
              rent >= 1500 ? 'Excellent' : rent >= 1000 ? 'Good' : rent > 0 ? 'Fair' : '—'
            )}

            {card(
              cfGradient,
              'Cash Flow',
              <div><div className="font-bold">Rent - Mortgage - Expenses</div><div className="opacity-75 font-mono text-xs">{formatCurrency(rent)} - {formatCurrency(mortgage)} - {formatCurrency(expTotal)} = {formatCurrency(cf)}/mo</div></div>,
              <div className="flex items-baseline gap-1.5 flex-wrap">
                <span className="text-xl font-black leading-none">{formatCurrency(cf)}<span className="text-sm font-semibold opacity-75">/mo</span></span>
                <span className="text-sm font-bold opacity-75">{formatCurrency(annualCF)}<span className="text-xs opacity-75">/yr</span></span>
              </div>,
              cf >= 500 ? 'Excellent' : cf >= 300 ? 'Great' : cf >= 100 ? 'Good' : cf >= 0 ? 'Break-even' : 'Negative'
            )}

            {card(
              annualNoi > 0 ? 'bg-gradient-to-br from-cyan-500 to-teal-700' : 'bg-gradient-to-br from-slate-400 to-slate-600',
              'NOI (Annual)',
              <div><div className="font-bold">(Rent x 12) - Annual Expenses</div><div className="opacity-75">Operating income before mortgage. Used for cap rate.</div></div>,
              p.noi != null ? formatCurrency(p.noi) : '—',
              p.breakeven_years != null ? `Breakeven: ${p.breakeven_years.toFixed(1)} yrs` : undefined
            )}

            {card(
              (p.cap_rate || 0) >= 10 ? 'bg-gradient-to-br from-blue-600 to-blue-800' :
              (p.cap_rate || 0) >= 8  ? 'bg-gradient-to-br from-blue-500 to-blue-700' :
              (p.cap_rate || 0) >= 5  ? 'bg-gradient-to-br from-sky-400 to-blue-600' :
                                         'bg-gradient-to-br from-slate-400 to-slate-600',
              'Cap Rate',
              <div><div className="font-bold">Annual NOI / Value x 100</div><div className="opacity-75">Unleveraged return. 8%+ is often considered strong in many rental markets.</div></div>,
              p.cap_rate ? formatPercent(p.cap_rate) : '—',
              (p.cap_rate || 0) >= 10 ? 'Excellent' : (p.cap_rate || 0) >= 8 ? 'Strong' : (p.cap_rate || 0) >= 5 ? 'Fair' : 'Weak'
            )}

            {card(
              roiVal >= 20 ? 'bg-gradient-to-br from-violet-600 to-purple-800' :
              roiVal >= 15 ? 'bg-gradient-to-br from-violet-500 to-purple-700' :
              roiVal >= 10 ? 'bg-gradient-to-br from-purple-400 to-violet-600' :
              roiVal > 0   ? 'bg-gradient-to-br from-slate-400 to-slate-600' :
                             'bg-gradient-to-br from-red-500 to-rose-600',
              'Cash-on-Cash',
              <div><div className="font-bold">Annual Cash Flow / Cash Invested x 100</div><div className="opacity-75">Return on actual out-of-pocket dollars.</div></div>,
              p.roi != null ? formatPercent(p.roi) : 'N/A',
              roiVal >= 20 ? 'Excellent' : roiVal >= 15 ? 'Great' : roiVal >= 10 ? 'Good' : roiVal > 0 ? 'Low' : 'Negative'
            )}

            {p.break_even_price != null && displayPrice != null
              ? (() => {
                  const bep = p.break_even_price!;
                  const under = displayPrice <= bep;
                  const diff = Math.abs(displayPrice - bep);
                  return card(
                    under ? 'bg-gradient-to-br from-teal-500 to-cyan-700' : 'bg-gradient-to-br from-red-500 to-rose-700',
                    'Max Buy Price',
                    <div><div className="font-bold">Break-even Purchase Price</div><div className="opacity-75">Price where Rent = Mortgage + All Expenses.</div></div>,
                    formatCurrency(bep),
                    under ? `${formatCurrency(diff)} under` : `${formatCurrency(diff)} over`
                  );
                })()
              : card('bg-gradient-to-br from-slate-400 to-slate-600', 'Max Buy Price', 'Not yet calculated.', '—')
            }

          </div>

          {(mortgage > 0 || expTotal > 0) && (
            <div className="mt-3 pt-3 border-t border-gray-100 space-y-1.5 text-sm">
              {mortgage > 0 && (
                <div className="flex justify-between"><span className="text-gray-500">Monthly Mortgage</span><span className="font-semibold">{formatCurrency(mortgage)}</span></div>
              )}
              {expTotal > 0 && (
                <div className="flex justify-between"><span className="text-gray-500">Monthly Expenses</span><span className="font-semibold">{formatCurrency(expTotal)}</span></div>
              )}
              {eb && (
                <details className="cursor-pointer mt-1">
                  <summary className="text-xs font-medium text-gray-600">Expense Breakdown</summary>
                  <div className="mt-1.5 space-y-1 pl-2 border-l-2 border-gray-200 text-xs text-gray-500">
                    {eb.property_tax > 0 && <div className="flex justify-between"><span>Property Tax</span><span>{formatCurrency(eb.property_tax)}/mo</span></div>}
                    {eb.insurance > 0 && <div className="flex justify-between"><span>Insurance</span><span>{formatCurrency(eb.insurance)}/mo</span></div>}
                    {eb.vacancy > 0 && <div className="flex justify-between"><span>Vacancy</span><span>{formatCurrency(eb.vacancy)}/mo</span></div>}
                    {eb.maintenance > 0 && <div className="flex justify-between"><span>Maintenance</span><span>{formatCurrency(eb.maintenance)}/mo</span></div>}
                    {eb.property_mgmt > 0 && <div className="flex justify-between"><span>Mgmt</span><span>{formatCurrency(eb.property_mgmt)}/mo</span></div>}
                    {eb.capex_reserve > 0 && <div className="flex justify-between"><span>CapEx</span><span>{formatCurrency(eb.capex_reserve)}/mo</span></div>}
                    {(eb.pmi ?? 0) > 0 && <div className="flex justify-between"><span>PMI</span><span>{formatCurrency(eb.pmi)}/mo</span></div>}
                  </div>
                </details>
              )}
            </div>
          )}
        </div>

        {/* Analysis Accordion — same as list view */}
        <div className="px-4 pb-4">
          <AnalysisAccordion property={propertyForAccordion} onPropertyUpdate={() => {}} />
        </div>
      </div>
    </div>
  );
};

export default PropertyPopup;
